/**
 * ActionMenu Component
 *
 * A dropdown menu component that renders via React Portal to avoid
 * clipping issues with parent container overflow.
 */
import { MoreHorizontal } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface ActionMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  variant?: 'default' | 'danger' | 'success' | 'warning';
  disabled?: boolean;
  hidden?: boolean;
}

export interface ActionMenuProps {
  items: (ActionMenuItem | 'divider')[];
  id: string;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  buttonClassName?: string;
  menuClassName?: string;
}

const getVariantClasses = (variant?: string) => {
  switch (variant) {
    case 'danger':
      return 'text-red-600 hover:bg-red-50';
    case 'success':
      return 'text-green-700 hover:bg-green-50';
    case 'warning':
      return 'text-orange-700 hover:bg-orange-50';
    default:
      return 'text-gray-700 hover:bg-gray-50';
  }
};

export const ActionMenu: React.FC<ActionMenuProps> = ({
  items,
  id,
  isOpen: controlledIsOpen,
  onOpenChange,
  buttonClassName,
  menuClassName,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Support both controlled and uncontrolled modes
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const setIsOpen = onOpenChange || setInternalIsOpen;

  // Calculate menu position
  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const menuWidth = 192; // w-48 = 12rem = 192px
    const menuHeight = 300; // Approximate max height
    const padding = 8;

    let top = rect.bottom + 4;
    let left = rect.right - menuWidth;

    // Adjust if menu would go off right edge
    if (left + menuWidth > window.innerWidth - padding) {
      left = window.innerWidth - menuWidth - padding;
    }

    // Adjust if menu would go off left edge
    if (left < padding) {
      left = padding;
    }

    // Adjust if menu would go off bottom edge
    if (top + menuHeight > window.innerHeight - padding) {
      top = rect.top - menuHeight - 4;
      if (top < padding) {
        top = padding;
      }
    }

    setMenuPosition({ top, left });
  }, []);

  // Handle toggle
  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isOpen) {
      updatePosition();
    }
    setIsOpen(!isOpen);
  };

  // Handle click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    // Update position on scroll/resize
    const handlePositionUpdate = () => {
      updatePosition();
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handlePositionUpdate, true);
    window.addEventListener('resize', handlePositionUpdate);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handlePositionUpdate, true);
      window.removeEventListener('resize', handlePositionUpdate);
    };
  }, [isOpen, setIsOpen, updatePosition]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, setIsOpen]);

  // Filter out hidden items
  const visibleItems = items.filter(item => item === 'divider' || !item.hidden);

  // Remove consecutive dividers and leading/trailing dividers
  const cleanedItems = visibleItems.filter((item, index, arr) => {
    if (item !== 'divider') return true;
    if (index === 0 || index === arr.length - 1) return false;
    if (arr[index - 1] === 'divider') return false;
    return true;
  });

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className={
          buttonClassName || 'p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded'
        }
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls={`action-menu-${id}`}
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>

      {isOpen &&
        createPortal(
          <>
            {/* Invisible backdrop for click outside */}
            <div
              className="fixed inset-0 z-[9998]"
              onClick={() => setIsOpen(false)}
              aria-hidden="true"
            />
            {/* Menu */}
            <div
              ref={menuRef}
              id={`action-menu-${id}`}
              className={`fixed z-[9999] w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 ${menuClassName || ''}`}
              style={{
                top: menuPosition.top,
                left: menuPosition.left,
              }}
              role="menu"
              aria-orientation="vertical"
            >
              {cleanedItems.map((item, index) => {
                if (item === 'divider') {
                  return <hr key={`divider-${index}`} className="my-1 border-gray-100" />;
                }

                return (
                  <button
                    key={`${item.label}-${index}`}
                    onClick={e => {
                      e.stopPropagation();
                      item.onClick();
                      setIsOpen(false);
                    }}
                    disabled={item.disabled}
                    className={`w-full px-3 py-2 text-left text-xs flex items-center ${getVariantClasses(item.variant)} ${
                      item.disabled ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    role="menuitem"
                  >
                    {item.icon && <span className="mr-2 flex-shrink-0">{item.icon}</span>}
                    {item.label}
                  </button>
                );
              })}
            </div>
          </>,
          document.body
        )}
    </>
  );
};

export default ActionMenu;
